import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';

import {
  MarketingLeadListQuery,
  useMarketingLeadListQuery,
} from 'core/graphql';

import { useLeadPermissions } from '../Crm/useLeadPermissions';
import { marketingGqlFilter, marketingQueryErrorCode } from './marketingApi';
import { IMarketingFilter } from './types';

/**
 * ТЗ 0106 — лиды окна с именем: строка канала, кампании, семейства или
 * оффера «Маркетинга» → «Лиды (N)» → список. Ключи строки — как у динамики
 * лидов: `none` — нет значения, канал `unknown` — не определён.
 */

export type IMarketingLeadRow = MarketingLeadListQuery['marketingLeads']['items'][number];

/** Строк на странице списка (предел сервера — 200). */
export const MARKETING_LEADS_PAGE = 25;

/** Ключи строки разреза, по которым сужается список. */
export interface IMarketingLeadsKeys {
  channel?: string | null;
  subChannel?: string | null;
  campaign?: string | null;
  family?: string | null;
  offer?: string | null;
}

/** Что открыть: ключи строки + подпись строки для заголовка панели. */
export interface IMarketingLeadsRequest {
  keys: IMarketingLeadsKeys;
  label: string;
  /** Сколько лидов показала строка (`crmLeads`) — в заголовок до ответа. */
  count?: number | null;
}

const NO_ROWS: IMarketingLeadRow[] = [];

/**
 * Фильтр раздела → фильтр списка. Объявления (`adIds`) сервер у списка лидов
 * не принимает — лиды не привязаны к объявлению.
 */
export const marketingLeadsFilter = (filter: IMarketingFilter) =>
  marketingGqlFilter({ ...filter, adIds: null });

export const useMarketingLeads = (
  filter: IMarketingFilter,
  keys: IMarketingLeadsKeys,
  offset: number,
  enabled = true,
) => {
  const { canJourney } = useLeadPermissions();
  const skip = !canJourney || !enabled;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useMarketingLeadListQuery({
    variables: {
      filter: marketingLeadsFilter(filter),
      channel: keys.channel || null,
      subChannel: keys.subChannel || null,
      campaign: keys.campaign || null,
      family: keys.family || null,
      offer: keys.offer || null,
      limit: MARKETING_LEADS_PAGE,
      offset,
    },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const page = (data ?? previousData)?.marketingLeads;
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    rows: page?.items ?? NO_ROWS,
    total: page?.total ?? 0,
    loading: loading && !page,
    fetching: loading,
    failed: Boolean(error) && !page,
    errorCode: marketingQueryErrorCode(error as ApolloError | undefined),
    reload,
  };
};
